import { openPopup } from '@neb/popup';

import {
  REPLACE_INSURANCE_PAYER_ERROR,
  REPLACE_INSURANCE_PAYER_SUCCESS,
} from '../../../../src/utils/user-message';
import { getPatientAppointmentsFromInsurance } from '../../../neb-api-client/src/patient-api-client';
import { replaceInsurancePayer } from '../../../neb-api-client/src/patient-insurance-api-client';
import { openError, openSuccess } from '../../../neb-dialog/neb-banner-state';
import { POPUP_RENDER_KEYS } from '../../../neb-popup/src/renderer-keys';
import { store } from '../../../neb-redux/neb-redux-store';
import { parseDate } from '../../../neb-utils/date-util';

import { openOverlay, OVERLAY_KEYS } from './overlay-constants';

const isEmpty = value =>
  value === null || value === undefined || value.toString().length === 0;

const isMet = value => value !== null && value !== '' && value === '$0.00';

const isNumeric = n => !Number.isNaN(parseFloat(n)) && Number.isFinite(n);

const convertCurrency = amount => {
  if (isEmpty(amount)) return '';
  return amount ? Number(amount.replace(/[^0-9.]/g, '')) : 0;
};

const formatCurrency = amount => {
  if (isEmpty(amount)) return '';
  return amount
    ? convertCurrency(String(amount)).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      })
    : '$0.00';
};

const __convertUpperCase = description => {
  const converted = description
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return converted;
};

const getMatchingServiceTypes = (serviceTypes, allServiceTypes) => {
  const matchingServiceTypes = serviceTypes.reduce(
    (memo, { serviceTypeId }) => {
      const match = allServiceTypes.find(({ id }) => id === serviceTypeId);
      if (match) memo.push(match);
      return memo;
    },
    [],
  );
  return matchingServiceTypes
    .map(
      ({ code, description }) => `${code} - ${__convertUpperCase(description)}`,
    )
    .join(', ');
};

const INSURANCE_LEVELS = Object.freeze({
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
  TERTIARY: 'Tertiary',
});

const sortServiceTypes = serviceTypes =>
  serviceTypes.sort(({ code: a }, { code: b }) =>
    (isNumeric(a) ? `0${a}` : a).localeCompare(
      isNumeric(b) ? `0${b}` : b,
      'en',
      { numeric: true },
    ),
  );

const SEVEN_YEARS_AGO = parseDate().subtract(7, 'years').startOf('day');

const getFromDate = date => {
  if (parseDate(date).isBefore(SEVEN_YEARS_AGO)) {
    return SEVEN_YEARS_AGO;
  }

  return parseDate(date);
};

const getDateFilters = patientInsurance => {
  const to =
    patientInsurance.end && parseDate(patientInsurance.end) < parseDate()
      ? parseDate(patientInsurance.end)
      : parseDate();

  const from = patientInsurance.start
    ? getFromDate(patientInsurance.start)
    : parseDate(to).subtract(30, 'days');

  return { to, from };
};

const replacePatientInsurancePayer = async patientInsurance => {
  const result = await openPopup(POPUP_RENDER_KEYS.REPLACE_INSURANCE_PAYER, {
    payerPlan: patientInsurance.payerPlan,
    planInfo: patientInsurance.planInfo,
  });

  if (!result) {
    return false;
  }

  try {
    const { to, from } = getDateFilters(patientInsurance);

    const appointments = await getPatientAppointmentsFromInsurance({
      patientId: patientInsurance.patientId,
      insuranceId: patientInsurance.id,
      from,
      to,
    });

    let appointmentIds = [];

    if (appointments?.length) {
      const normalizeDate = date => (date ? parseDate(date) : null);

      const payerPlan = `(${patientInsurance.payerPlan.alias}) ${patientInsurance.payerPlan.payerName}`;
      const appointmentsResult = await openOverlay(
        OVERLAY_KEYS.PATIENT_INSURANCE_BILL_TYPE_UPDATE,
        {
          patientId: patientInsurance.patientId,
          appointments,
          payerPlan,
          insurance: patientInsurance.planInfo.planName,
          insuranceId: patientInsurance.id,
          dateRange: {
            from,
            to,
          },
          insuranceDates: {
            from: normalizeDate(patientInsurance.start),
            to: normalizeDate(patientInsurance.end),
          },
        },
      );

      appointmentIds = appointmentsResult?.appointmentIds || [];
    }

    await replaceInsurancePayer({
      patientId: patientInsurance.patientId,
      insuranceId: patientInsurance.id,
      newPayerId: result.payerPlanId,
      appointmentIds,
    });

    store.dispatch(openSuccess(REPLACE_INSURANCE_PAYER_SUCCESS));
    return true;
  } catch (error) {
    store.dispatch(openError(REPLACE_INSURANCE_PAYER_ERROR));
    console.error(error);
    return false;
  }
};

export {
  convertCurrency,
  formatCurrency,
  getMatchingServiceTypes,
  sortServiceTypes,
  isEmpty,
  isMet,
  INSURANCE_LEVELS,
  SEVEN_YEARS_AGO,
  getDateFilters,
  replacePatientInsurancePayer,
};
